// src/pages/PairAnalysisPage.jsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  analyzePairs,
  deletePairTemplate,
  getPairTemplateById,
  getPairTemplates,
  savePairTemplate
} from '../api/apiClient';
import AnalysisSettings from '../components/analysis/AnalysisSettings';
import AnalysisResultsTable from '../components/analysis/AnalysisResultsTable';
import AnalysisTemplatesPanel from '../components/analysis/AnalysisTemplatesPanel';
import Modal from '../components/shared/Modal';
import {
  buildTemplatePayload,
  defaultAnalysisSettings,
  mapAnalyzeRequestToSettings
} from '../components/analysis/analysisSettingsModel';
import { HiClock, HiExclamationCircle } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const extractTemplateEntity = (payload) => {
  if (payload?.template && typeof payload.template === 'object') return payload.template;
  if (payload?.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const normalizeTemplateList = (payload) => {
  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.templates)
      ? payload.templates
      : Array.isArray(payload?.items)
        ? payload.items
        : [];

  return source
    .map((item) => extractTemplateEntity(item))
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = new Date(left?.updatedAt || left?.createdAt || 0).getTime();
      const rightTime = new Date(right?.updatedAt || right?.createdAt || 0).getTime();
      return rightTime - leftTime;
    });
};

const getTemplateId = (template) =>
  template?.publicTemplateId || template?.templateId || template?._id || template?.id || null;

const getTemplateName = (template) =>
  String(template?.templateName || template?.name || '').trim();

const PairAnalysisPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [settings, setSettings] = useState(defaultAnalysisSettings);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentParams, setCurrentParams] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesRefreshing, setTemplatesRefreshing] = useState(false);
  const [templateNameDraft, setTemplateNameDraft] = useState('');
  const [templateStatus, setTemplateStatus] = useState(null);
  const [isTemplateSaving, setIsTemplateSaving] = useState(false);
  const [applyingTemplateId, setApplyingTemplateId] = useState(null);
  const [deleteTemplateState, setDeleteTemplateState] = useState({ open: false, template: null, loading: false });
  const [activeTemplateId, setActiveTemplateId] = useState(null);

  const canManageTemplates = Boolean(user?.login);

  const refreshTemplates = useCallback(async ({ silent = false } = {}) => {
    if (!canManageTemplates) {
      setTemplates([]);
      return [];
    }

    if (silent) {
      setTemplatesRefreshing(true);
    } else {
      setTemplatesLoading(true);
    }

    try {
      const data = await getPairTemplates();
      const normalized = normalizeTemplateList(data);
      setTemplates(normalized);
      return normalized;
    } catch (err) {
      setTemplateStatus({
        type: 'error',
        text: err.message || t('analysis.templates.errors.load')
      });
      return [];
    } finally {
      setTemplatesLoading(false);
      setTemplatesRefreshing(false);
    }
  }, [canManageTemplates, t]);

  useEffect(() => {
    if (!canManageTemplates) {
      setTemplates([]);
      setTemplateNameDraft('');
      setActiveTemplateId(null);
      return;
    }

    refreshTemplates();
  }, [canManageTemplates, refreshTemplates]);

  const existingTemplateWithDraftName = useMemo(() => {
    const normalizedDraft = templateNameDraft.trim().toLowerCase();
    if (!normalizedDraft) return null;

    return templates.find((template) => getTemplateName(template).toLowerCase() === normalizedDraft) || null;
  }, [templateNameDraft, templates]);

  const handleAnalyze = async (params) => {
    setIsLoading(true);
    setError(null);
    setResults([]);
    setStats(null);
    setCurrentParams({
      ...params,
      margin: settings.margin,
      leverage: settings.leverage
    });

    try {
      const data = await analyzePairs(params);
      if (data && data.results) {
        setResults(data.results);
        setStats({ requested: data.requested_pairs, processed: data.processed_pairs });
      } else {
        setResults([]);
      }
    } catch (err) {
      setError(err.name === 'AbortError' ? t('analysis.error_timeout') : err.message || 'Error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    const templateName = templateNameDraft.trim();

    if (!templateName) {
      setTemplateStatus({
        type: 'error',
        text: t('analysis.templates.errors.name_required')
      });
      return;
    }

    setIsTemplateSaving(true);
    setTemplateStatus(null);

    try {
      await savePairTemplate(buildTemplatePayload(templateName, settings));
      const refreshedTemplates = await refreshTemplates({ silent: true });
      const matchedTemplate = refreshedTemplates.find(
        (template) => getTemplateName(template).toLowerCase() === templateName.toLowerCase()
      );

      setActiveTemplateId(getTemplateId(matchedTemplate));
      setTemplateStatus(null);
    } catch (err) {
      setTemplateStatus({
        type: 'error',
        text: err.message || t('analysis.templates.errors.save')
      });
    } finally {
      setIsTemplateSaving(false);
    }
  };

  const handleApplyTemplate = async (template) => {
    const templateId = getTemplateId(template);
    if (!templateId) return;

    setApplyingTemplateId(templateId);
    setTemplateStatus(null);

    try {
      const response = await getPairTemplateById(templateId);
      const entity = extractTemplateEntity(response);
      const mergedTemplate = entity && typeof entity === 'object'
        ? { ...template, ...entity }
        : template;

      setSettings(mapAnalyzeRequestToSettings(mergedTemplate));
      setTemplateNameDraft(getTemplateName(mergedTemplate));
      setActiveTemplateId(templateId);
      setTemplateStatus(null);
    } catch (err) {
      setTemplateStatus({
        type: 'error',
        text: err.message || t('analysis.templates.errors.apply')
      });
    } finally {
      setApplyingTemplateId(null);
    }
  };

  const handleDeleteTemplate = async () => {
    const template = deleteTemplateState.template;
    const templateId = getTemplateId(template);

    if (!templateId) {
      setDeleteTemplateState({ open: false, template: null, loading: false });
      return;
    }

    setDeleteTemplateState((prev) => ({ ...prev, loading: true }));
    setTemplateStatus(null);

    try {
      await deletePairTemplate(templateId);
      await refreshTemplates({ silent: true });

      if (activeTemplateId === templateId) {
        setActiveTemplateId(null);
      }

      setTemplateStatus(null);
      setDeleteTemplateState({ open: false, template: null, loading: false });
    } catch (err) {
      setDeleteTemplateState((prev) => ({ ...prev, loading: false }));
      setTemplateStatus({
        type: 'error',
        text: err.message || t('analysis.templates.errors.delete')
      });
    }
  };

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-black text-white tracking-tight">{t('analysis.page_title')}</h1>
        <p className="text-slate-400 text-sm">{t('analysis.page_desc')}</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.45fr)_380px]">
        <AnalysisSettings
          settings={settings}
          onSettingsChange={setSettings}
          onAnalyze={handleAnalyze}
          isLoading={isLoading}
        />

        <AnalysisTemplatesPanel
          templates={templates}
          templateName={templateNameDraft}
          onTemplateNameChange={setTemplateNameDraft}
          onSave={handleSaveTemplate}
          onApply={handleApplyTemplate}
          onDelete={(template) => setDeleteTemplateState({ open: true, template, loading: false })}
          onRefresh={() => refreshTemplates({ silent: true })}
          statusMessage={templateStatus}
          isLoading={templatesLoading}
          isRefreshing={templatesRefreshing}
          isSaving={isTemplateSaving}
          applyingTemplateId={applyingTemplateId}
          activeTemplateId={activeTemplateId}
          canManage={canManageTemplates}
          isUpdatingName={Boolean(existingTemplateWithDraftName)}
        />
      </div>

      {error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-sm flex items-center gap-3">
          <HiExclamationCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="py-24 flex flex-col items-center justify-center bg-slate-800/30 rounded-3xl border border-slate-700/50">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-6" />
          <h2 className="text-xl font-bold text-white mb-2">{t('analysis.btn_loading')}</h2>
          <div className="flex items-center gap-2 text-slate-400 bg-slate-900/50 px-4 py-2 rounded-full border border-slate-700">
            <HiClock className="text-blue-400" />
            <span className="text-sm">{t('analysis.wait_notice')}</span>
          </div>
        </div>
      ) : results.length > 0 ? (
        <div className="space-y-4">
          <div className="flex justify-between items-end pl-2 text-white">
            <h2 className="text-xl font-bold">{t('analysis.results_title')} ({results.length})</h2>
            {stats ? (
              <span className="text-xs text-slate-500 font-mono">
                {t('analysis.processed')}: {stats.processed} / {stats.requested}
              </span>
            ) : null}
          </div>
          <AnalysisResultsTable data={results} analysisParams={currentParams} />
        </div>
      ) : !error && (
        <div className="py-24 text-center border-2 border-dashed border-slate-700 rounded-3xl bg-slate-800/30">
          <p className="text-slate-500 font-medium italic">{t('analysis.placeholder')}</p>
        </div>
      )}

      <Modal
        isOpen={deleteTemplateState.open}
        onClose={() => {
          if (deleteTemplateState.loading) return;
          setDeleteTemplateState({ open: false, template: null, loading: false });
        }}
        title={t('analysis.templates.delete_title')}
      >
        <div className="space-y-5">
          <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
            {t('analysis.templates.delete_message', {
              name: getTemplateName(deleteTemplateState.template) || t('analysis.templates.untitled')
            })}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteTemplateState({ open: false, template: null, loading: false })}
              disabled={deleteTemplateState.loading}
              className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-2 text-sm font-bold text-slate-300 transition-all hover:border-slate-500 hover:text-white disabled:opacity-60"
            >
              {t('analysis.templates.cancel')}
            </button>
            <button
              type="button"
              onClick={handleDeleteTemplate}
              disabled={deleteTemplateState.loading}
              className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-black text-white transition-all hover:bg-rose-500 disabled:cursor-not-allowed disabled:bg-slate-700"
            >
              {deleteTemplateState.loading ? t('analysis.templates.deleting') : t('analysis.templates.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PairAnalysisPage;
